import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SellerModeProvider } from "./contexts/SellerModeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import OrdersPage from "./pages/OrdersPage";
import ReturnsPage from "./pages/ReturnsPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import InventoryPage from "./pages/InventoryPage";
import BulkUploadPage from "./pages/BulkUploadPage";
import WarehousesPage from "./pages/WarehousesPage";
import ReconciliationPage from "./pages/ReconciliationPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import KycPage from "./pages/KycPage";
import AdminKycPage from "./pages/AdminKycPage";
import AdminCategoriesPage from "./pages/AdminCategoriesPage";
import AdminCommissionsPage from "./pages/AdminCommissionsPage";
import AdminCatalogPage from "./pages/AdminCatalogPage";
import AdminSellersPage from "./pages/AdminSellersPage";
import ShippingPage from "./pages/ShippingPage";
import AdminPenaltiesPage from "./pages/AdminPenaltiesPage";
import ControlTowerPage from "./pages/ControlTowerPage";
import TicketsPage from "./pages/TicketsPage";
import PerformancePage from "./pages/PerformancePage";
import AuditLogsPage from "./pages/AuditLogsPage";
import ApprovalWorkflowsPage from "./pages/ApprovalWorkflowsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import PerformanceAlertsPage from "./pages/PerformanceAlertsPage";
import ReviewsPage from "./pages/ReviewsPage";
import SellerComparisonPage from "./pages/SellerComparisonPage";
import OpsDashboardPage from "./pages/OpsDashboardPage";
import StaffManagementPage from "./pages/StaffManagementPage";
import HolidayManagementPage from "./pages/HolidayManagementPage";
import TatConfigPage from "./pages/TatConfigPage";
import PincodeMatrixPage from "./pages/PincodeMatrixPage";
import SlotManagementPage from "./pages/SlotManagementPage";
import StoreHierarchyPage from "./pages/StoreHierarchyPage";
import QuickCommercePage from "./pages/QuickCommercePage";
import TrainingPage from "./pages/TrainingPage";
import MessageCenterPage from "./pages/MessageCenterPage";
import BundlesPage from "./pages/BundlesPage";
import PricingRulesPage from "./pages/PricingRulesPage";
import CarrierRulesPage from "./pages/CarrierRulesPage";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";
import VendorManagementPage from "./pages/VendorManagementPage";
import BrandingPage from "./pages/BrandingPage";
import InvoiceTemplatesPage from "./pages/InvoiceTemplatesPage";
import LanguageConfigPage from "./pages/LanguageConfigPage";
import SellerToolsPage from "./pages/SellerToolsPage";
import GettingStartedPage from "./pages/GettingStartedPage";
import OnboardingAdminPage from "./pages/OnboardingAdminPage";
import SellerOnboardingWizardPage from "./pages/SellerOnboardingWizardPage";
import AiAssistant from "./components/AiAssistant";

import "./App.css";

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#fcfcfc]">
    <div className="spinner"></div>
  </div>
);

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user || user === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Public route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Router component that checks for session_id
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/returns" element={<ProtectedRoute><ReturnsPage /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/products/new" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
      <Route path="/products/:productId" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/inventory/bulk-upload" element={<ProtectedRoute><BulkUploadPage /></ProtectedRoute>} />
      <Route path="/warehouses" element={<ProtectedRoute><WarehousesPage /></ProtectedRoute>} />
      <Route path="/reconciliation" element={<ProtectedRoute><ReconciliationPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><KycPage /></ProtectedRoute>} />
      <Route path="/admin/kyc" element={<ProtectedRoute><AdminKycPage /></ProtectedRoute>} />
      <Route path="/admin/categories" element={<ProtectedRoute><AdminCategoriesPage /></ProtectedRoute>} />
      <Route path="/admin/commissions" element={<ProtectedRoute><AdminCommissionsPage /></ProtectedRoute>} />
      <Route path="/admin/catalog" element={<ProtectedRoute><AdminCatalogPage /></ProtectedRoute>} />
      <Route path="/admin/sellers" element={<ProtectedRoute><AdminSellersPage /></ProtectedRoute>} />
      <Route path="/admin/penalties" element={<ProtectedRoute><AdminPenaltiesPage /></ProtectedRoute>} />
      <Route path="/admin/control-tower" element={<ProtectedRoute><ControlTowerPage /></ProtectedRoute>} />
      <Route path="/shipping" element={<ProtectedRoute><ShippingPage /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><TicketsPage /></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogsPage /></ProtectedRoute>} />
      <Route path="/admin/approval-workflows" element={<ProtectedRoute><ApprovalWorkflowsPage /></ProtectedRoute>} />
      <Route path="/admin/performance-alerts" element={<ProtectedRoute><PerformanceAlertsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/reviews" element={<ProtectedRoute><ReviewsPage /></ProtectedRoute>} />
      <Route path="/admin/seller-comparison" element={<ProtectedRoute><SellerComparisonPage /></ProtectedRoute>} />
      <Route path="/admin/ops" element={<ProtectedRoute><OpsDashboardPage /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute><StaffManagementPage /></ProtectedRoute>} />
      <Route path="/holidays" element={<ProtectedRoute><HolidayManagementPage /></ProtectedRoute>} />
      <Route path="/tat-config" element={<ProtectedRoute><TatConfigPage /></ProtectedRoute>} />
      <Route path="/pincodes" element={<ProtectedRoute><PincodeMatrixPage /></ProtectedRoute>} />
      <Route path="/slots" element={<ProtectedRoute><SlotManagementPage /></ProtectedRoute>} />
      <Route path="/stores" element={<ProtectedRoute><StoreHierarchyPage /></ProtectedRoute>} />
      <Route path="/quick-commerce" element={<ProtectedRoute><QuickCommercePage /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessageCenterPage /></ProtectedRoute>} />
      <Route path="/bundles" element={<ProtectedRoute><BundlesPage /></ProtectedRoute>} />
      <Route path="/pricing-rules" element={<ProtectedRoute><PricingRulesPage /></ProtectedRoute>} />
      <Route path="/carrier-rules" element={<ProtectedRoute><CarrierRulesPage /></ProtectedRoute>} />
      <Route path="/scheduled-reports" element={<ProtectedRoute><ScheduledReportsPage /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute><VendorManagementPage /></ProtectedRoute>} />
      <Route path="/admin/branding" element={<ProtectedRoute><BrandingPage /></ProtectedRoute>} />
      <Route path="/invoice-templates" element={<ProtectedRoute><InvoiceTemplatesPage /></ProtectedRoute>} />
      <Route path="/language-config" element={<ProtectedRoute><LanguageConfigPage /></ProtectedRoute>} />
      <Route path="/seller-tools" element={<ProtectedRoute><SellerToolsPage /></ProtectedRoute>} />
      <Route path="/getting-started" element={<ProtectedRoute><GettingStartedPage /></ProtectedRoute>} />
      <Route path="/seller-onboarding" element={<ProtectedRoute><SellerOnboardingWizardPage /></ProtectedRoute>} />
      <Route path="/admin/onboarding" element={<ProtectedRoute><OnboardingAdminPage /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SellerModeProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </SellerModeProvider>
    </AuthProvider>
  );
}

export default App;
